import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { EVENT_TYPES, type EventType, type MyEvent, useMyEvents } from '../api/events';
import { useMembership } from '../auth/session';
import { FilterChips } from '../components/FilterChips';
import { ErrorView } from '../components/Guards';
import { dateLocale } from '../i18n';
import { daysUntil, formatDayMonth, formatDayShort, formatWeekday } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './EventsPage.module.css';

/**
 * Chorist: their upcoming events, like the director's list but about themselves: whether they take part
 * (in neutral words), their answer, how many of the songs they are ready on, and their next deadline.
 */
export function MyEventsPage() {
  const { t } = useTranslation();
  const membership = useMembership();
  const events = useMyEvents(membership?.choirId);
  const [filter, setFilter] = useState<EventType | null>(null);

  if (!membership) return null;
  const list = events.data ?? [];
  const shown = filter ? list.filter((e) => e.type === filter) : list;

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <div className={ui.eyebrow}>{membership.choirName}</div>
          <h1>{t('events.title')}</h1>
          <div className={ui.subtitle}>{t('events.subtitle', { count: list.length })}</div>
        </div>
      </div>

      <FilterChips
        label={t('events.colType')}
        value={filter}
        onChange={setFilter}
        options={[
          { key: null, label: t('events.all'), count: list.length },
          ...EVENT_TYPES.map((type) => ({ key: type, label: t(`eventTypes.${type}`), count: list.filter((e) => e.type === type).length })),
        ]}
      />

      <section className={ui.card}>
        {events.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {events.isError && <ErrorView onRetry={() => void events.refetch()} />}
        {events.data && shown.length === 0 && <p className={ui.empty}>{t('events.empty')}</p>}
        {shown.length > 0 && (
          <table className={ui.table}>
            <thead>
              <tr>
                <th>{t('events.colDate')}</th>
                <th>{t('events.colEvent')}</th>
                <th>{t('events.colType')}</th>
                <th>{t('myEventsPage.colYou')}</th>
                <th>{t('myEventsPage.colSongs')}</th>
                <th>{t('events.colDeadline')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** Whether the chorist takes part, in the same neutral words as the home page. */
export function participationText(event: MyEvent, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (event.participation === 'NOT_IN_LINEUP') return t('myEvents.notInLineup');
  if (event.participation === 'EXCLUDED') return t('myEvents.excluded');
  return [event.answer ? t(`myEvents.yourAnswer.${event.answer}`) : t('myEvents.awaiting'), event.limitedLineup ? t('myEvents.inLineup') : null]
    .filter(Boolean)
    .join(' · ');
}

/** The chorist's own next date: the reply when they have not answered, else the soonest requested submission. */
function myNextDeadline(event: MyEvent): { label: string; song?: string; date: string } | null {
  if (event.participation !== 'IN') return null;
  if (!event.answer && event.replyDeadline && daysUntil(event.replyDeadline) >= 0) return { label: 'reply', date: event.replyDeadline };
  if (event.answer === 'NOT_ATTENDING') return null;
  const next = event.songs
    .filter((s) => s.submissionRequested && !s.submittedSinceRequest && s.submissionDeadline && daysUntil(s.submissionDeadline) >= 0)
    .sort((a, b) => (a.submissionDeadline as string).localeCompare(b.submissionDeadline as string))[0];
  return next ? { label: 'submission', song: next.title, date: next.submissionDeadline as string } : null;
}

function EventRow({ event }: { event: MyEvent }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const ready = event.songs.filter((s) => s.grade === 'READY').length;
  const deadline = myNextDeadline(event);
  const days = deadline ? daysUntil(deadline.date) : null;
  return (
    <tr data-testid="my-event-row">
      <td>
        <strong>{event.endsOn ? `${formatDayShort(event.startsOn, locale)} – ${formatDayShort(event.endsOn, locale)}` : formatDayShort(event.startsOn, locale)}</strong>
        <span className={ui.secondary}>{formatWeekday(event.startsOn, locale)}</span>
      </td>
      <td>
        <Link to={`/events/${event.id}`}>{event.name}</Link>
        {event.venue && <span className={ui.secondary}>{event.venue}</span>}
      </td>
      <td>
        <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
      </td>
      <td className={event.participation === 'IN' && !event.answer ? styles.awaiting : undefined}>{participationText(event, t)}</td>
      <td>
        {!event.readinessTracking ? (
          <span className={ui.muted}>{t('events.attendanceOnly')}</span>
        ) : event.songs.length === 0 ? (
          <span className={ui.muted}>{t('events.noRepertoire')}</span>
        ) : (
          t('myEvents.songsReady', { count: event.songs.length, ready })
        )}
      </td>
      <td>
        {deadline ? (
          <>
            <span className={ui.secondary}>{deadline.label === 'reply' ? t('events.deadline.reply') : t('events.deadline.submission', { song: deadline.song })}</span>
            <strong className={days !== null && days <= 14 ? styles.soon : undefined}>
              {days === 0 ? t('events.today') : days === 1 ? t('events.tomorrow') : formatDayMonth(deadline.date, locale)}
              {days !== null && days > 1 && days <= 14 && ` · ${t('events.days', { count: days })}`}
            </strong>
          </>
        ) : (
          <span className={ui.muted}>–</span>
        )}
      </td>
    </tr>
  );
}
